import { Space, Typography } from 'antd';
import Tag from 'components/new-ui/base/tag/Tag';
import { ICS2CodelistsMap } from 'store/codelists/code';
import { ICS2InformationExchangeMessages } from 'store/declarations/eu/ics2/ICS2InformationExchangeMessages';
import { Ics2CustomsRequest, Ics2Referral } from 'store/declarations/eu/ics2/customs-requests/types';
import { getRequestIssueDate } from 'store/declarations/eu/ics2/customs-requests/utils';
import useIcs2CodelistLabel from 'hooks/useIcs2CodelistLabel';
import { formatRequestIssueDate, getReferralTypeLabel, getRequestTagStatus } from './referralLabels';

type Props = {
    request: Ics2CustomsRequest;
    referral: Ics2Referral;
};

const ReferralSummary = ({ request, referral }: Props) => {
    const getLabel = useIcs2CodelistLabel([ICS2CodelistsMap.CL013, ICS2CodelistsMap.CL724, ICS2CodelistsMap.CL752]);

    const details = referral.requestDetails;
    const issuedAt = formatRequestIssueDate(getRequestIssueDate(request));
    const houseDocument = details?.transportDocumentHouse?.documentNumber;
    const isScreening = request.messageType === ICS2InformationExchangeMessages.IE3Q03;

    const requested = details?.additionalInformation ?? [];
    const supporting = details?.supportingDocuments ?? [];
    const recommended = (details?.recommendedHRCMScreeningMethod ?? [])
        .map((entry) => entry.method)
        .filter(Boolean) as string[];

    const screeningText = recommended.length
        ? `Customs recommend ${recommended
              .map((method) => getLabel(method, ICS2CodelistsMap.CL724))
              .join(
                  ', '
              )}. Record the outcome, the methods actually used and, where relevant, who authorised the screening.`
        : 'Screen the cargo using at least two approved methods, then record the outcome and the methods used.';

    return (
        <Space direction="vertical" size={8} style={{ width: '100%' }}>
            <Space size={8} wrap>
                <Tag status={getRequestTagStatus(request.messageType)} label={request.messageType} />
                <Typography.Text strong>{getReferralTypeLabel(referral.requestType)}</Typography.Text>
                {issuedAt && <Typography.Text type="secondary">· {issuedAt}</Typography.Text>}
            </Space>

            <Typography.Text>
                Referral Request: <strong>{referral.referralRequestReference}</strong>
            </Typography.Text>

            {houseDocument && (
                <Typography.Text>
                    House Transport Document: <strong>{houseDocument}</strong>
                </Typography.Text>
            )}

            {/* The exact element Customs is asking about, when it chose to point at one. */}
            {details?.pointer?.messageElementPath && (
                <Typography.Text>
                    Data Element: <strong>{details.pointer.messageElementPath}</strong>
                </Typography.Text>
            )}

            {isScreening ? (
                <Typography.Text>{screeningText}</Typography.Text>
            ) : requested.length ? (
                <ul style={{ marginBottom: 0, paddingLeft: 20, listStyleType: 'initial' }}>
                    {requested.map((entry, index) => (
                        <li key={`${entry.code}-${index}`}>
                            {entry.code ? getLabel(entry.code, ICS2CodelistsMap.CL752, true) : null}
                            {entry.code && entry.text ? ' - ' : null}
                            {entry.text}
                        </li>
                    ))}
                </ul>
            ) : (
                <Typography.Text type="secondary">
                    Customs did not attach a specific request code to this referral.
                </Typography.Text>
            )}

            {supporting.length > 0 && (
                <Typography.Text>
                    Customs referenced{' '}
                    {supporting
                        .map((document) =>
                            [document.referenceNumber, getLabel(document.type, ICS2CodelistsMap.CL013)]
                                .filter(Boolean)
                                .join(' - ')
                        )
                        .join(', ')}
                    .
                </Typography.Text>
            )}
        </Space>
    );
};

export default ReferralSummary;
